import { useCallback, useLayoutEffect, useRef, useState } from 'react';

interface PillRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface UseSlidingPillResult {
  /** Attach to the tab group container (must be positioned `relative`). */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Spread onto each tab element: `ref={registerTab(tabId)}`. */
  registerTab: (key: string) => (node: HTMLElement | null) => void;
  /** Geometry of the active tab, or null before first measure. */
  rect: PillRect | null;
  /** False on the first positioning, so the pill doesn't fly in from the left. */
  animate: boolean;
}

/**
 * Measures the active tab so a single absolutely-positioned pill can slide
 * between tabs instead of the background snapping from one tab to the next.
 *
 * Measured rather than computed as `index * 100%` because only some of our tab
 * groups are equal-width (`flex-1`) — the dashboard and mode tabs size to their
 * content, so their pill has to match each tab's real width.
 */
export function useSlidingPill(activeKey: string): UseSlidingPillResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLElement | null>>({});
  const [rect, setRect] = useState<PillRect | null>(null);
  // Tracked in a ref so enabling the transition never triggers a re-render.
  const hasMeasured = useRef(false);
  const [animate, setAnimate] = useState(false);

  const registerTab = useCallback(
    (key: string) => (node: HTMLElement | null) => {
      tabRefs.current[key] = node;
    },
    [],
  );

  useLayoutEffect(() => {
    const node = tabRefs.current[activeKey];
    const container = containerRef.current;
    if (!node || !container) return;

    const measure = () => {
      setRect({
        left: node.offsetLeft,
        top: node.offsetTop,
        width: node.offsetWidth,
        height: node.offsetHeight,
      });
      if (!hasMeasured.current) {
        hasMeasured.current = true;
        // Enable the transition only after the pill is already in place.
        requestAnimationFrame(() => setAnimate(true));
      }
    };

    measure();

    // Labels can reflow (font swap, container resize, a count badge changing
    // width), which would leave the pill stranded at a stale position.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeKey]);

  return { containerRef, registerTab, rect, animate };
}
